import { useCallback, useEffect, useState } from "react";
import { t } from "ttag";
import { useStoreActions } from "../../models/RootStore";
import { isNgDevice } from "../DeviceSync/deviceSyncConfig";
import { formatSyncError, isMongoDeviceId } from "../DeviceSync/deviceSyncUtils";

// Same normaliser as the module-local pickDevice in DeviceSync.tsx: the admin device
// endpoints answer with either a device or a keyed map of devices.
const pickDevice = (payload: any): any => {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  if (payload.id || payload._id) {
    return payload;
  }
  const values = Object.values(payload);
  return values.find((item: any) => item && (item.id || item._id)) || values[0] || null;
};

/**
 * Resolves the `:deviceId` segment the way the sync page does: it may be a Mongo id
 * or a device serial, and only NG devices are accepted. `deviceId` is the resolved
 * Mongo id, which is what every API call must use.
 */
export function useNgDevice(deviceParam: string) {
  const getDeviceById = useStoreActions((actions) => actions.devices.getDeviceById);
  const getDevicesBySerial = useStoreActions((actions) => actions.devices.getDevicesBySerial);
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);

  const [device, setDevice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fail = (message: string) => {
      setDevice(null);
      setError(message);
      addMessage({ message });
    };

    const resolveDevice = async () => {
      setLoading(true);
      setError("");
      try {
        let nextDevice: any = null;
        if (isMongoDeviceId(deviceParam)) {
          nextDevice = pickDevice(await getDeviceById(deviceParam));
        } else {
          const bySerial = await getDevicesBySerial({ serial: deviceParam });
          const list = Array.isArray(bySerial) ? bySerial : Object.values(bySerial || {});
          nextDevice = list.find((item: any) => item?.serial === deviceParam) || pickDevice(bySerial);
        }

        if (cancelled) {
          return;
        }
        if (!nextDevice) {
          fail(t`Device not found.`);
          return;
        }
        if (!isNgDevice(nextDevice)) {
          fail(t`This page is for NG devices only.`);
          return;
        }
        setDevice(nextDevice);
      } catch (err) {
        if (!cancelled) {
          fail(formatSyncError(err, t`Couldn't load this device.`));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    resolveDevice();
    return () => {
      cancelled = true;
    };
  }, [deviceParam, getDeviceById, getDevicesBySerial, addMessage]);

  const rawId = device?.id || device?._id || "";
  const deviceId = rawId ? String(rawId) : "";

  // Same as the sync page: re-read only the connection fields, keep everything else.
  const refreshDeviceStatus = useCallback(async () => {
    if (!deviceId) {
      return;
    }
    try {
      const nextDevice = pickDevice(await getDeviceById(deviceId));
      if (!nextDevice) {
        return;
      }
      setDevice((current: any) => {
        if (!current) {
          return nextDevice;
        }
        if (
          current.isConnected === nextDevice.isConnected &&
          current.disconnectedTimestamp === nextDevice.disconnectedTimestamp
        ) {
          return current;
        }
        return {
          ...current,
          isConnected: nextDevice.isConnected,
          disconnectedTimestamp: nextDevice.disconnectedTimestamp
        };
      });
    } catch {
      // Keep the last known connection status.
    }
  }, [deviceId, getDeviceById]);

  return { device, setDevice, deviceId, loading, error, refreshDeviceStatus };
}
